/**
 * Resolve the API base URL used by the frontend.
 *
 * Production Netlify builds have historically baked a bare Railway host
 * (https://…railway.app) without the `/api` suffix via site env overrides.
 * That makes every left-nav call hit the SPA HTML fallback and explode with
 * `Unexpected token '<' … is not valid JSON`.
 *
 * On our public frontend hosts we always prefer same-origin `/api` so Netlify's
 * proxy to Railway is used, regardless of a bad REACT_APP_API_BASE_URL.
 */

function stripTrailingSlashes(value: string): string {
  return value.replace(/\/+$/, '');
}

export function normalizeApiBaseUrl(raw?: string | null): string {
  let base = stripTrailingSlashes((raw || '').trim());
  if (!base) return '/api';

  // Absolute backend host missing /api (the live production bug)
  if (/^https?:\/\//i.test(base) && !/\/api$/i.test(base)) {
    return `${base}/api`;
  }

  // Relative root or odd relative path
  if (base.startsWith('/') && base !== '/api' && !base.endsWith('/api')) {
    return base === '/' ? '/api' : `${base}/api`;
  }

  return base;
}

export function resolveApiBaseUrl(): string {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname.toLowerCase();
    if (
      host === 'tripnovac-visa.com' ||
      host === 'www.tripnovac-visa.com' ||
      host.endsWith('.netlify.app')
    ) {
      return '/api';
    }
  }

  const fromEnv = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || '';
  return normalizeApiBaseUrl(fromEnv || 'http://localhost:4000/api');
}

/** Parse a fetch Response as JSON only when the body is actually JSON. */
export async function readJsonResponse<T = any>(res: Response): Promise<T> {
  const contentType = (res.headers.get('content-type') || '').toLowerCase();
  const text = await res.text();
  const looksJson = contentType.includes('application/json') || text.trim().startsWith('{') || text.trim().startsWith('[');
  if (!looksJson) {
    throw new Error(
      `Expected JSON from ${res.url || 'API'} but received ${contentType || 'non-JSON'} (HTTP ${res.status})`
    );
  }
  return JSON.parse(text) as T;
}
